import { ClientProposalOwnerConfirmedQuotationInputDto } from '@repo/utils'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { IDepositFormValues } from '../../owner-can-confirm-booking/owner-can-confirm-booking-modal/OwnerCanConfirmBookingModal'
import DepositSection from '../../upload-quotation-modal/deposit-section/DepositSection'
import { getMinDepositRate } from '../../upload-quotation-modal/deposit-section/utils/get-min-deposit-rate'
import { IUploadQuotationProps } from '../../upload-quotation-modal/UploadQuotationModal'
import {
  ConfirmQuotationModalClientProposalFragment,
  PdfTypeEnum,
} from '../../../../../../../../lib/graphql'
import { errorToast } from '../../../../../../../../utils'
import { multiplyByFactor } from '../../../../../../../../utils/prices'
import UploadField from '../../../../../../../atoms/fields/upload-field/UploadField'
import { CurrencyAreaContextProvider } from '../../../../../../../contexts'
import { Modal } from '../../../../../../../molecules'
import { BillingColumns } from '../../../../../../../organisms/proposal/proposal-details/details-container/BillingColumns'
import { DescriptionMode } from '../../../../../../../organisms/proposal/proposal-details/details-container/DescriptionMode'
import DetailsTotalOwner from '../../../../../../../organisms/proposal/proposal-details/details-container/details-total-owner/DetailsTotalOwner'
import DetailsContainer from '../../../../../../../organisms/proposal/proposal-details/details-container/DetailsContainer'
import PdfCheckbox from '../../../buttons-by-step/utils/pdf-checkbox/PdfCheckbox'
import { onUploadFile } from '../../../utils/utils'

const ConfirmQuotationModal = ({
  isOpen,
  setIsOpen,
  ownerConfirmedQuotation,
  clientProposal,
  uploadQuotationData,
}: {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  ownerConfirmedQuotation: {
    callback: (args: {
      ownerConfirmedQuotationInputs: ClientProposalOwnerConfirmedQuotationInputDto
    }) => void
    isLoading: boolean
  }
  clientProposal: ConfirmQuotationModalClientProposalFragment
  uploadQuotationData?: IUploadQuotationProps
}) => {
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { specialConditionsUrl, setSpecialConditionsUrl } =
    uploadQuotationData ?? {}
  const [specialConditionsFileName, setSpecialConditionsFileName] =
    React.useState<string | undefined>(
      specialConditionsUrl ? `CGV - ${clientProposal.house?.title}` : undefined,
    )

  const totalUser = clientProposal.price?.totalUser
  const ownerFeesPrice = totalUser?.feesWithDiscount?.ownerFeesPrice
  const priceWithDiscount = totalUser?.priceWithDiscount
  const priceWithDiscountAndOwnerFees = totalUser?.priceWithDiscountAndOwnerFees

  if (!ownerFeesPrice || !priceWithDiscount || !priceWithDiscountAndOwnerFees) {
    return null
  }

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useForm<IDepositFormValues>({
    mode: 'onChange',
    defaultValues: {
      isConfirmed: false,
    },
  })

  const ProposalDetailsTotalColumns = [
    BillingColumns.description,
    BillingColumns.totalHT,
    BillingColumns.totalTTC,
  ]

  const onSubmit = async (formValues: IDepositFormValues) => {
    if (!specialConditionsUrl) {
      errorToast([
        intl.formatMessage({
          id: 'ui.ConfirmQuotationModel.uploadCGV',
          defaultMessage: 'Veuillez charger vos CGV',
        }),
      ])
    }

    const data: ClientProposalOwnerConfirmedQuotationInputDto = {
      depositRate: multiplyByFactor(
        Math.max(
          parseInt(formValues?.depositRate || '0'),
          getMinDepositRate(clientProposal.startDate),
        ),
      ),
      specialConditions: {
        url: specialConditionsUrl ?? '',
        fileName: specialConditionsFileName ?? '',
      },
    }

    await ownerConfirmedQuotation.callback({
      ownerConfirmedQuotationInputs: data,
    })

    setIsOpen(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      title={intl.formatMessage({
        id: 'ui.ConfirmQuotationModal.modalTitle',
        defaultMessage: "Valider mon devis et définir l'acompte",
      })}
      styleHeader={{ marginBottom: '20px' }}
      footer={{
        button: {
          label: intl.formatMessage({
            id: 'ui.ConfirmQuotationModal.confirmQuote',
            defaultMessage: 'Confirmer mon devis',
          }),
          fullWidth: true,
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          isLoading: ownerConfirmedQuotation.isLoading || isLoading,
        },
      }}
      scrollbarStyle={{
        display: 'flex',
        flexDirection: 'column',
        paddingInline: '30px',
        paddingTop: 0,
      }}
      closeModal={() => setIsOpen(false)}
      footerStyle={{ boxSizing: 'border-box' }}
      renderButtons={() => (
        <PdfCheckbox
          name='isConfirmed'
          control={control}
          clientProposalId={clientProposal._id}
          clientRequestId={clientProposal.clientRequestId}
          setIsLoading={setIsLoading}
          pdfCheckboxText={[
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText',
                defaultMessage:
                  'En cochant cette case, vous confirmez avoir lu et accepté les ',
              }),
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText2',
                defaultMessage: 'Conditions générales d’utilisation',
              }),
              url: `${process.env.NEXT_PUBLIC_APP_URL}/conditions-generales`,
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText3',
                defaultMessage: ', le ',
              }),
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText4',
                defaultMessage: 'Contrat de Mandat',
              }),
              pdfType: PdfTypeEnum.OWNER_MANDATE_CONTRACT_UNSIGNED,
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText5',
                defaultMessage: ', et le ',
              }),
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText6',
                defaultMessage: 'Devis',
              }),
              pdfType: PdfTypeEnum.OWNER_QUOTATION_UNSIGNED,
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText7',
                defaultMessage: ' et reconnaissez que vos ',
              }),
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText8',
                defaultMessage:
                  'Conditions Générales d’Exécution de la prestation',
              }),
              pdfType: PdfTypeEnum.OWNER_SPECIAL_CONDITIONS,
            },
            {
              label: intl.formatMessage({
                id: 'ui.ConfirmQuotationModal.pdfCheckboxText9',
                defaultMessage:
                  ' déposées sur le site de Naboo constituent vos dernières conditions générales applicables à l’exécution de la prestation.',
              }),
            },
          ]}
        />
      )}
    >
      <CurrencyAreaContextProvider
        context={{ currencyArea: clientProposal.house?.currencyArea }}
      >
        <div className={s.content}>
          <h3 className='text-base font-bold'>
            <FormattedMessage
              id='ui.ConfirmQuotationModal.uploadSpecialConditionsBelow'
              defaultMessage='Chargez ci-dessous vos Conditions Générale de Vente'
            />
          </h3>
          <UploadField
            label={intl.formatMessage({
              id: 'ui.ConfirmQuotationModal.uploadSpecialCodnitions',
              defaultMessage: 'Charger mes CGV',
            })}
            onClear={(field) => {
              field.onChange(null)
              setSpecialConditionsFileName(undefined)
              setSpecialConditionsUrl && setSpecialConditionsUrl(undefined)
            }}
            onFileChange={(e) =>
              onUploadFile({
                event: e,
                onUploadFile:
                  uploadQuotationData?.handleSpecialConditionsFormData,
                setFileName: setSpecialConditionsFileName,
                setFileUrl: setSpecialConditionsUrl,
              })
            }
            url={specialConditionsUrl}
            setUrl={setSpecialConditionsUrl}
            fileName={specialConditionsFileName}
            name='specialConditions'
            control={control}
            required={true}
          />
          <DepositSection
            control={control}
            startDate={clientProposal.startDate}
            depositRate={getValues('depositRate')}
          />
          <DetailsContainer
            title={intl.formatMessage({
              id: 'ui.ConfirmQuotationModal.total',
              defaultMessage: 'Total',
            })}
            columns={ProposalDetailsTotalColumns}
            descriptionMode={DescriptionMode.hidden}
          >
            <DetailsTotalOwner
              total={{
                price: priceWithDiscount,
                ownerFees: ownerFeesPrice,
                priceOwner: priceWithDiscountAndOwnerFees,
              }}
              className={s.detailsTotal}
              columns={ProposalDetailsTotalColumns}
              descriptionMode={DescriptionMode.toggable}
              showAdjustments={false}
            />
          </DetailsContainer>
        </div>
      </CurrencyAreaContextProvider>
    </Modal>
  )
}

export default ConfirmQuotationModal
